import polyline from '@mapbox/polyline';
import Box from '@mui/material/Box';
import React, { useEffect, useState } from 'react';

import config from '../../config';
import { lazyLoadWithDimensions } from '../../util/contextHelpers';
import { circlePolyline } from '../../util/maps';
import AnimatedLogo from '../Logo/VanSite/AnimatedLogo';

const formatColor = (color) => {
  return color.replace(/^#/, '');
};

const fuzzyCircleOverlay = (center, mapsConfig) => {
  const strokeWeight = 1;
  const strokeColor = mapsConfig.fuzzy.circleColor;
  const strokeOpacity = 0.5;
  const fillColor = mapsConfig.fuzzy.circleColor;
  const fillOpacity = 0.2;

  const path = circlePolyline(center, mapsConfig.fuzzy.offset);
  const styles = `-${strokeWeight}+${formatColor(strokeColor)}-${strokeOpacity}+${formatColor(
    fillColor
  )}-${fillOpacity}`;
  return `path${styles}(${encodeURIComponent(polyline.encode(path))})`;
};

const customMarkerOverlay = (center, mapsConfig) => {
  const { url } = mapsConfig.customMarker;
  return `url-${encodeURIComponent(url)}(${center.lng},${center.lat})`;
};

const markerOverlay = (center) => {
  return `pin-s(${center.lng},${center.lat})`;
};

const mapOverlay = (center, mapsConfig) => {
  if (mapsConfig.fuzzy.enabled) {
    return fuzzyCircleOverlay(center, mapsConfig);
  }
  if (mapsConfig.customMarker.enabled) {
    return customMarkerOverlay(center, mapsConfig);
  }
  return markerOverlay(center);
};

const StaticMapboxMap = ({ address = '', center = null, zoom = config.maps.fuzzy.enabled ? config.maps.fuzzy.defaultZoomLevel : 11, mapsConfig = config.maps, dimensions }) => {
  const { width, height } = dimensions;
  const [isLoading, setIsLoading] = useState(false);
  const [image, setImage] = useState(null);

  const overlay = mapOverlay(center, mapsConfig);

  const src =
    'https://api.mapbox.com/styles/v1/mapbox/streets-v10/static' +
    (overlay ? `/${overlay}` : '') +
    `/${center.lng},${center.lat},${zoom}` +
    `/1280x${height}` +
    `?access_token=${config.maps.mapboxAccessToken}`;

  useEffect(() => {
    setIsLoading(true);
    const imageToLoad = new Image();
    imageToLoad.src = src;
    imageToLoad.onload = (event) => {
      setIsLoading(false);
      setImage(event.target);
    };
  }, [src]);

  // NOTE: no window.mapboxgl gate here. This is a plain <img> against the Mapbox
  // Static Images API, authenticated with config.maps.mapboxAccessToken — it does
  // not need the deferred mapbox-gl script. Gating on it made the static map
  // disappear whenever that script was slow or failed to load.

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <AnimatedLogo />
      </Box>
    );
  }

  return (
    <img alt={address} src={src} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
  );
};

export default lazyLoadWithDimensions(StaticMapboxMap, { maxWidth: '100%' });
